'use client';
import { HTTP_STATUS_CODE } from '@payments-checkout/shared/helpers/constants/http-status.const';
import { useHandleRedirectByError } from '@payments-checkout/shared/helpers/hooks/useHandleRedirectByError';
import { ObjectChecker } from '@payments-checkout/shared/helpers/managers/objectChecker';
import { usePaymentMethod } from '@payments-checkout/shared/providers';
import { notFound, useParams } from 'next/navigation';
import { useEffect } from 'react';
import { getDefaultPaymentMethod } from '../../helpers/getDefaultPaymentMethod.helper';
import { usePaymentLinkId } from '../usePaymentLinkId';
import { usePreselectVariant } from '../usePreselectVariant';
import { SharedPageBuilder } from './usePageShared.helpers';

export const usePageSharedData = () => {
  const params = useParams<{ paymentLinkId: string }>();
  const { paymentMethod, installments, brand, selectedDigitalWallet } =
    usePaymentMethod();
  const { skipPreselect } = usePreselectVariant();
  const paymentLinkIdData = usePaymentLinkId(params.paymentLinkId);
  const { redirectByCode } = useHandleRedirectByError();

  useEffect(() => {
    const status = paymentLinkIdData.error?.response?.status;

    if (status === HTTP_STATUS_CODE.NOT_FOUND) {
      notFound();
      return;
    }
    if (status) {
      redirectByCode(status);
    }
  }, [paymentLinkIdData.error?.response?.status]);

  const isReady =
    paymentLinkIdData.isFetched &&
    ObjectChecker.isFilled(paymentLinkIdData.data);

  if (isReady) {
    const useBrand =
      paymentLinkIdData.data?.payment_settings.credit_card_settings
        ?.use_brand_interest_rate;
    const detailItems = SharedPageBuilder.itemsBuilder(
      paymentLinkIdData.data?.cart_settings.items
    );
    const isRecurrence = !!paymentLinkIdData.data?.cart_settings?.recurrences;

    const { items, total } = SharedPageBuilder.summaryBuilderWithConditions({
      data: paymentLinkIdData.data,
      selectedPaymentMethod:
        paymentMethod ||
        getDefaultPaymentMethod(
          paymentLinkIdData.data?.payment_settings.accepted_payment_methods ?? [],
          skipPreselect
        ),
      brand,
      installments,
      useBrand,
    });

    const installmentOptions = SharedPageBuilder.installmentsBuilder({
      value: paymentLinkIdData?.data?.payment_settings?.credit_card_settings,
      brand: brand,
      organization: paymentLinkIdData?.data?.account_settings.organization,
      totalCost: paymentLinkIdData?.data?.cart_settings.total_cost,
    });

    const hasAnyDigitalWallet =
      paymentLinkIdData.data &&
      SharedPageBuilder.hasAnyDigitalWallet(paymentLinkIdData.data);

    const isDeliveryEnabled =
      paymentLinkIdData.data?.delivery_settings?.enabled;

    const showAddressPage = paymentLinkIdData.data?.flow_settings?.show_shipping_address_page;

    return {
      paymentLinkIdData,
      isReady,
      detailItems,
      items,
      total,
      paymentLinkId: params.paymentLinkId,
      installmentOptions,
      hasAnyDigitalWallet,
      useBrandRate: useBrand,
      hasInvalidPaymentSelected: !paymentMethod && !selectedDigitalWallet,
      isRecurrence,
      isDeliveryEnabled,
      showAddressPage
    };
  }

  return {
    paymentLinkIdData,
    isReady,
    isDeliveryEnabled: false,
    showAddressPage: false,
    detailItems: [],
    detailRecurrenceItems: [],
    items: [],
    total: { description: 'total', formattedAmount: '--', amount: 0 },
    paymentLinkId: params.paymentLinkId,
    installmentOptions: [],
    hasAnyDigitalWallet: false,
    useBrandRate: false,
    hasInvalidPaymentSelected: !paymentMethod && !selectedDigitalWallet,
  };
};
